import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, Subject, debounceTime, first, firstValueFrom, interval, map, of, takeUntil, tap, timer } from 'rxjs';
import Comparator from 'src/app/common/comparators/comparator';
import { buildFilterArray, download } from 'src/app/common/utils';
import { DownloadService } from 'src/app/data/download.service';
import { UserService } from 'src/app/data/user.service';
import { Filter } from 'src/app/models/common/filter';
import { FilterPresetDto } from 'src/app/models/dtos/filter-preset-dto';
import { WorkerStatusEnum } from 'src/app/models/enums/WorkerStatusEnum';
import { FilterTypeEnum } from 'src/app/models/enums/filter-type-enum';
import { FilterPresetNameFormDialogComponent } from 'src/app/shared/components/filters/filter-preset-name-form-dialog/filter-preset-name-form-dialog.component';
import { PdfViewerService } from 'src/app/shared/services/pdf-viewer.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { autocompleteValidator } from 'src/app/shared/validators/autocomplete.validator';
import { EFolderGridItemDto } from 'src/app/models/dtos/e-folder-grid-item-dto';
import { EDocumentationService } from 'src/app/data/e-documentation-service';
import { AddEDocumentFormComponent, AddEDocumentFormData } from '../add-e-document-form/add-e-document-form.component';
import { FileService } from 'src/app/shared/services/file-service';
import { Messages } from 'src/app/common/enums';
import { EDocumentDetailsComponent } from '../e-document-details/e-document-details.component';
import { EFoldersUcpListDataSource } from './e-folders-ucp-list.datasource';
import { EFoldersUcpListFiltersComponent } from '../e-folders-ucp-list-filters/e-folders-ucp-list-filters.component';
@Component({
  selector: 'app-e-folders-ucp-list',
  templateUrl: './e-folders-ucp-list.component.html',
  styleUrls: ['./e-folders-ucp-list.component.scss'],
  providers: [DatePipe],
  standalone: false
})
export class EFoldersUcpListComponent implements OnInit {
  sticky = false;
  @ViewChild(CdkVirtualScrollViewport, { static: true }) viewport: CdkVirtualScrollViewport;
  @ViewChild('tableContainer', { static: true }) tableContainerRef: ElementRef;

  height = 0;

  page = 1;
  actualPageStart = 1;
  actualPageEnd = 1;
  pageSize = 40;
  offset$: Observable<number>;

  filtersFormGroup: FormGroup;
  displayedColumns: string[] = [
    'select',
    'FullName',
    'Pesel',
    'Document',
    'ValidUntilDateFrom',
    'ValidUntilDateTo',
    'AddedDate',
    'Comments',
    'Actions'
  ];

  private _areFiltersExpanded: boolean;
  public get areFiltersExpanded(): boolean {
    return this._areFiltersExpanded;
  }
  public set areFiltersExpanded(value: boolean) {
    if (this._areFiltersExpanded !== value) {
      this._areFiltersExpanded = value;
      this.checkViewportSize();
    }
  }

  dataSource: EFoldersUcpListDataSource;

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('filters', { static: true }) filtersElementRef: ElementRef;

  public readonly defaultFilterPresetName = 'default';
  public filterPresets: FilterPresetDto[];
  private _selectedFilterPresetId: number;
  public set selectedFilterPresetId(value: number) {
    this._selectedFilterPresetId = value;
    if (value) {
      this.applyFilterPreset(this.filterPresets.find(fp => fp.Id === value));
    }
  }
  public get selectedFilterPresetId(): number {
    return this._selectedFilterPresetId;
  }
  public get selectedFilterPreset(): FilterPresetDto {
    return this.filterPresets.find(fp => fp.Id === this.selectedFilterPresetId);
  }
  public get showFilterPresetSelector(): boolean {
    return this.filterPresets && (this.filterPresets.length > 1 || (this.filterPresets.length === 1 && this.filterPresets[0].Name !== this.defaultFilterPresetName));
  }
  public get isSavedFilterPreset(): boolean {
    return this.selectedFilterPreset && this.selectedFilterPreset.Name !== this.defaultFilterPresetName;
  }
  public get scrollItemPosition(): number {
    return this.viewport.getRenderedRange().end;
  }

  public async setSelectedFilterPresetId(value: number): Promise<void> {
    this.selectedFilterPresetId = value;
    await this.filterData(buildFilterArray(this.filtersFormGroup, EFoldersUcpListFiltersComponent.operatorsMap));
    await firstValueFrom(this.userService.selectFilterPreset(value));
  }

  //private multipleActionsConfig: MultipleFoldersActionConfigDto;
  private readonly defaultPageSize: number = 1000;
  private readonly selectionLengthLimitWarning: number = 50;
  public readonly defaultSortColumn: string = 'AddedDate';
  public readonly defaultSortDirection: string = 'desc';
  private readonly unsubscribe$ = new Subject<void>();

  private filters: Filter[] = [];

  public readonly workerStatusEnum = WorkerStatusEnum;

  constructor(
    private eDocumentationService: EDocumentationService,
    private translateService: TranslateService,
    private formBuilder: FormBuilder,
    public datepipe: DatePipe,
    private changeDetectorRef: ChangeDetectorRef,
    private userService: UserService,
    private dialog: MatDialog,
    private spinner: NgxSpinnerService,
    private snackbarService: SnackBarService,
    private downloadService: DownloadService,
    private pdfViewerService: PdfViewerService,
    private fileService: FileService
  ) {
  }

  async ngOnInit(): Promise<void> {
    this.height = this.tableContainerRef.nativeElement.clientHeight;
    this.init();
    this.buildFormGroup();
    await this.restoreSavedFiltersPreset();
    this.initFiltersFormChangeObserver();
    this.filters = buildFilterArray(this.filtersFormGroup, EFoldersUcpListFiltersComponent.operatorsMap);
    this.fetchData();
    this.initRefreshDataEveryTenSecond();
    this.initLangChanegeObserver();
    this.initSelectionChangeObserver();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  filterData(filters: Filter[]) {
    this.filters = filters;
    this.resetData();
    this.fetchData();
  }

  onSortChange() {
    this.resetData();
    this.fetchData();
  }

  async onRowChxChange(event: MatCheckboxChange, row: EFolderGridItemDto) {
    if (event) {
      this.dataSource.selection.toggle(row);

      if (this.isAllSelected()) {
        await this.getMultipleActionsConfig();
      }

      this.changeDetectorRef.detectChanges();
    }
  }

  toggleFiltersPanel = () => (this.areFiltersExpanded = !this.areFiltersExpanded);

  isAllSelected = () => this.dataSource.isAllSelected;

  async onSelectAllChxChange(): Promise<void> {
    if (this.isAllSelected()) {
      this.dataSource.deselectAll();
    } else {
      this.dataSource.selectAll();
      await this.getMultipleActionsConfig();
    }
  }

  onPreviewBtnClick(element: EFolderGridItemDto) {
    this.dialog.open(EDocumentDetailsComponent, {
      panelClass: 'details-dialog',
      data: {
        dto: element,
      },
    });
  }

  onPreviewFileBtnClick(element: EFolderGridItemDto) {
    const endpoint = `e-documentations/files`;
    this.fileService.showFile(element.OriginalFileName, element.Id, endpoint);
  }

  onDownloadBtnClick(element: EFolderGridItemDto) {
    this.downloadService
      .getFileAsBlob('e-documentations/files', element.Id, element.OriginalFileName)
      .subscribe((srcUrl) => download(srcUrl, element.OriginalFileName));
  }

  onEditBtnClick(element: EFolderGridItemDto) {
    this.openModal(element);
  }

  onSendBtnClick(element: EFolderGridItemDto) {
    this.spinner.show();
    firstValueFrom(timer(1500, 2000)
      .pipe(first())
      .pipe(tap(() => {
        this.spinner.hide();
        this.snackbarService.openSuccessSnackBar(Messages.SuccesfullyAcceptedAbsence);
      })));
  }

  async resetFilters(): Promise<void> {
    this.filtersFormGroup.reset(undefined, { emitEvent: false });
    this.filterData([]);

    if (this.selectedFilterPreset && this.selectedFilterPreset.Name !== this.defaultFilterPresetName) {
      await this.deselectFilterPreset();
    }

    this.selectedFilterPresetId = undefined;
    this.upsertFilterPreset();
  }

  private async refreshData(): Promise<void> {
    this.fetchData();
    await this.getMultipleActionsConfig();
  }

  private async getSelectedYears(): Promise<number[]> {
    return await firstValueFrom(of([1]));
  }

  signSelected() {
    if (this.isSignSelectedEnabled()) {

    }
  }

  exportSelected() {
    if (this.isExportSelectedEnabled()) {

    }
  }

  deleteAndRecalculateSelected() {
    if (this.isDeleteAndRecalculateSelectedEnabled()) {

    }
  }

  deleteSelected() {
    if (this.isDeleteSelectedEnabled()) {

    }
  }

  isSignSelectedEnabled(): boolean {
    return !!this.dataSource.selection.selected.length;
  }

  isExportSelectedEnabled(): boolean {
    return !!this.dataSource.selection.selected.length;
  }

  isDeleteAndRecalculateSelectedEnabled(): boolean {
    return !!this.dataSource.selection.selected.length;
  }

  isDeleteSelectedEnabled(): boolean {
    return !!this.dataSource.selection.selected.length;
  }

  private buildFormGroup(): void {
    this.filtersFormGroup = this.formBuilder.group({
      companyId: [null],
      company: [null, [autocompleteValidator]],
      addedDateFrom: [null],
      validUntilDateFrom: [null],
      addedDateTo: [null],
      validUntilDateTo: [null],
      lastName: [null],
      firstName: [null],
      pesel: [null],
      documentId: [null],
      document: [null],
      part: [null]
    });
  }

  private async restoreSavedFiltersPreset() {
    this.filterPresets = await firstValueFrom(this.userService.getFilterPresets(FilterTypeEnum.EFoldersUcpList));

    if (this.filterPresets && this.filterPresets.length) {
      const selectedPreset = this.filterPresets.find(fp => fp.IsSelected);

      this.selectedFilterPresetId = selectedPreset?.Id;
    }
  }

  private initFiltersFormChangeObserver() {
    const comparator = new Comparator<any>();

    this.filtersFormGroup.valueChanges
      .pipe(takeUntil(this.unsubscribe$), debounceTime(1000))
      .subscribe(async () => {
        if (this.filtersFormGroup.invalid) {
          return;
        }

        const filters = this.nonNullValues(this.filtersFormGroup.getRawValue());

        this.selectedFilterPresetId = this.filterPresets.find(fp => comparator.equals(JSON.parse(fp.Object), filters))?.Id;

        if (!this.selectedFilterPresetId) {
          await this.upsertFilterPreset();
        }
      });
  }

  private applyFilterPreset(filterPreset) {
    const filters = JSON.parse(filterPreset?.Object);

    if (filters && Object.values(filters).some((v) => !!v)) {
      this.filtersFormGroup.reset(undefined, { emitEvent: false });
      this.filtersFormGroup.patchValue(filters, { emitEvent: false });
      this.areFiltersExpanded = this.areFiltersExpanded || this.selectedFilterPreset.Name === this.defaultFilterPresetName;
    }
  }

  private initRefreshDataEveryTenSecond() {
    interval(10000)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(_ => {
        this.dataSource.setAllPagesAsObsolete();
        this.fetchData();
      });
  }

  private initLangChanegeObserver() {
    this.translateService.onLangChange
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.dataSource.setAllPagesAsObsolete();
        this.fetchData();
      });
  }

  async nextBatch(index: number) {
    if (!this.sticky) {
      this.sticky = true;
    }
    const buffer = 20;
    const range = this.viewport.getRenderedRange();
    const start = range.start;
    const end = range.end + 1;

    this.actualPageStart = Math.max(Math.floor(start / this.pageSize) - 1, 1);
    this.actualPageEnd = Math.max(Math.floor(end / this.pageSize) + 1, 1);

    if (end + buffer > this.page * this.pageSize) {
      this.fetchData(this.actualPageEnd);
    }
  }

  calculateHeight() {
    this.filtersElementRef.nativeElement

    return 300;
  }

  private init() {
    if (this.dataSource) {
      return;
    }

    this.dataSource = new EFoldersUcpListDataSource(
      this.eDocumentationService,
      {
        viewport: this.viewport
      });

    this.offset$ = this.viewport.renderedRangeStream.pipe(
      map(() => -this.viewport.getOffsetToRenderedContentStart())
    );
  }

  trackBy(index: number, item: EFolderGridItemDto) {
    return item.Id;
  }

  private fetchData(page?: number) {
    const startPage = page ?? this.actualPageStart;
    const endPage = page ?? this.actualPageEnd;

    for (let pageNumber = startPage; pageNumber <= endPage; pageNumber++) {
      this.dataSource.fetch(
        pageNumber,
        this.pageSize ?? this.defaultPageSize,
        this.sort?.active ?? this.defaultSortColumn,
        this.sort?.direction ?? this.defaultSortDirection,
        this.filters
      );
    }
  }

  private resetData() {
    this.actualPageEnd = 1;
    this.page = 1;
    this.dataSource.reset();
  }

  private checkViewportSize() {
    if (this.dataSource) {
      this.changeDetectorRef.detectChanges();
      this.viewport.checkViewportSize();
    }
  }

  private async upsertFilterPreset(): Promise<void> {
    const selectedPreset = this.filterPresets.find(fp => (this.selectedFilterPresetId && this.selectedFilterPresetId === fp.Id) || fp.Name === this.defaultFilterPresetName);

    const filters = this.nonNullValues(this.filtersFormGroup.getRawValue());

    if (selectedPreset) {
      if (filters) {
        await firstValueFrom(this.userService.updateFilterPreset(selectedPreset.Id, {
          Name: selectedPreset.Name,
          FilterPresetObject: filters
        }));
      } else {
        await firstValueFrom(this.userService.deleteFilterPreset(selectedPreset.Id));
      }
    } else if (filters) {
      await firstValueFrom(this.userService.addFilterPreset({
        Name: this.defaultFilterPresetName,
        FilterTypeId: FilterTypeEnum.EFoldersUcpList,
        FilterPresetObject: filters
      }));
    } else {
      return;
    }
    this.filterPresets = await firstValueFrom(this.userService.getFilterPresets(FilterTypeEnum.EFoldersUcpList));
    this.selectedFilterPresetId = this.filterPresets?.find(fp => fp.IsSelected)?.Id;
  }

  private nonNullValues(obj): { [key: string]: unknown } {
    const properties = Object.entries(obj).filter(([key, value]) => value !== null && value && (!(value instanceof Array) || value.length));
    if (properties.length) {
      return Object.fromEntries(properties);
    }
  }

  public async saveFilterPresetButtonClick(): Promise<void> {
    if (this.isSavedFilterPreset) {

      const existingDefaultPreset = this.filterPresets.find(fp => fp.Name == this.defaultFilterPresetName && this.selectedFilterPresetId !== fp.Id);

      if (existingDefaultPreset) {
        await firstValueFrom(this.userService.deleteFilterPreset(existingDefaultPreset.Id));
      }

      this.selectedFilterPreset.Name = this.defaultFilterPresetName;
      await this.upsertFilterPreset();
    } else {
      var res = await firstValueFrom(this.dialog
        .open(FilterPresetNameFormDialogComponent, { panelClass: 'form-dialog' })
        .afterClosed());

      if (res) {
        if (this.selectedFilterPreset.Name === this.defaultFilterPresetName) {
          this.selectedFilterPreset.Name = res;
        }
        await this.upsertFilterPreset();
      }
    }
  }

  private async deselectFilterPreset(): Promise<void> {
    await firstValueFrom(this.userService.deselectFilterPreset(this.selectedFilterPresetId));
    this.selectedFilterPreset.IsSelected = false;
  }

  private async getMultipleActionsConfig() {
    if (this.isAllSelected()) {
      //this.multipleActionsConfig = await firstValueFrom(this.Folderservice.getMultipleFoldersActionConfig(this.filters));
    }
  }

  private initSelectionChangeObserver() {
    this.dataSource.selectionChange$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(async _ => {
        if (this.isAllSelected()) {
          await this.getMultipleActionsConfig();
        }
      });
  }

  openModal(dto?: EFolderGridItemDto, isReadOnly: boolean = false) {
    const data: AddEDocumentFormData = {
      dto,
      statusId: 4,
      isReadOnly
    };

    this.dialog
      .open(AddEDocumentFormComponent, {
        data,
      })
      .afterClosed()
      .pipe(first())
      .subscribe(async (isCreated: boolean) => {
        if (isCreated) {
          await this.resetData();
          await this.fetchData();
        }
      });
  }
}
